import type { QueryClient } from '@tanstack/react-query';

import { createInvoice } from '~/api/invoices';
import { queryKeys } from '~/api/queryKeys';
import {
  listRetryableInvoices,
  markPendingInvoiceFailure,
  removePendingInvoice,
} from '~/offline/draftDb';

let isDraining = false;

export interface DrainResult {
  attempted: number;
  succeeded: number;
  failed: number;
}

/**
 * Tenta enviar as emissões pendentes que ainda estão na fila de retry. Idempotente
 * em paralelo — se já estiver rodando, devolve resultado vazio. Cada falha incrementa
 * `attempts`; ao esgotar `MAX_DRAIN_ATTEMPTS`, `markPendingInvoiceFailure` move o item
 * para falha terminal, então ele deixa de aparecer aqui — sem loop infinito nem nota
 * presa como pendente pra sempre.
 */
export async function drainPendingInvoices(queryClient: QueryClient): Promise<DrainResult> {
  if (isDraining) return { attempted: 0, succeeded: 0, failed: 0 };
  isDraining = true;

  let succeeded = 0;
  let failed = 0;

  try {
    const pending = await listRetryableInvoices();
    for (const item of pending) {
      if (!item.id) continue;
      try {
        await createInvoice(item.payload, {
          asDraft: false,
          idempotencyKey: item.idempotencyKey,
        });
        await removePendingInvoice(item.id);
        succeeded += 1;
      } catch (error) {
        failed += 1;
        const message = error instanceof Error ? error.message : 'Falha desconhecida';
        await markPendingInvoiceFailure(item.id, message);
      }
    }

    if (succeeded > 0) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
    }

    return { attempted: pending.length, succeeded, failed };
  } finally {
    isDraining = false;
  }
}
